import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CALL_DIRECTIONS,
  CALL_OUTCOMES,
  CALL_SUBJECT_MAX_LENGTH,
  type CallDirection,
  type CallOutcome,
  type CreateCallInput,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useLogCall, type ActivitySubject } from '../../lib/activity/use-activity.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * "I just rang them" — a call that HAPPENED, recorded once and never edited.
 *
 * Deciding when to ring next is NOT in this form. That is a follow-up task, so a person has one
 * queue and the follow-ups list has one author.
 *
 * DIRECTION AND OUTCOME ARE SEPARATE QUESTIONS. "Who rang" and "what happened" are independent,
 * and keeping them apart is what lets the shop record a missed INBOUND call — the person who rang,
 * got voicemail, and was not rung back, which is the most expensive call in the book.
 *
 * The duration boxes appear only once the call CONNECTED: a three-minute call nobody answered is
 * a typo, and the server refuses it. The form simply never offers it.
 */

export interface LogCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The customer the call was with. */
  subject: ActivitySubject;
  /** Their name, for the dialog's description. */
  name: string;
  /** Preselects the direction, so "they rang us" opens this already answered. */
  defaultDirection?: CallDirection;
}

export function LogCallDialog({
  open,
  onOpenChange,
  // Renamed here: the form's own "subject" is what the call was about.
  subject: owner,
  name,
  defaultDirection = 'outbound',
}: LogCallDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();
  const log = useLogCall(owner);

  const [subject, setSubject] = useState('');
  const [direction, setDirection] = useState<CallDirection>(defaultDirection);
  const [outcome, setOutcome] = useState<CallOutcome>('reached');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSubject('');
    setDirection(defaultDirection);
    setOutcome('reached');
    setMinutes('');
    setSeconds('');
    setNotes('');
    setFormError(null);
  }, [open, defaultDirection]);

  const connected = outcome === 'reached';

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void (async () => {
      setFormError(null);

      // Minutes and seconds are two boxes for the person and ONE integer for the database —
      // stored in seconds the way money is stored in paise, because most calls run
      // under two minutes and rounding them to whole minutes throws the figure away.
      const totalSeconds =
        connected && (minutes !== '' || seconds !== '')
          ? Number(minutes || 0) * 60 + Number(seconds || 0)
          : null;

      const input: CreateCallInput = {
        subject: subject.trim(),
        direction,
        outcome,
        ...(totalSeconds !== null ? { durationSeconds: totalSeconds } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };

      try {
        await log.mutateAsync(input);
        toast({ title: t('activity.calls.logged'), variant: 'success' });
        onOpenChange(false);
      } catch (error) {
        if (isApiRequestError(error)) {
          setFormError(error.message);
          return;
        }
        setFormError(error instanceof Error ? error.message : t('error.unexpected'));
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="atelier-dialog max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('activity.calls.title')}</DialogTitle>
          <DialogDescription>{t('activity.calls.description', { name })}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {formError ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-subject`}>{t('activity.calls.subjectLabel')}</Label>
            <Input
              id={`${fieldId}-subject`}
              value={subject}
              maxLength={CALL_SUBJECT_MAX_LENGTH}
              onChange={(event) => setSubject(event.target.value)}
              placeholder={t('activity.calls.subjectPlaceholder')}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-direction`}>{t('activity.calls.directionLabel')}</Label>
              <select
                id={`${fieldId}-direction`}
                value={direction}
                onChange={(event) => setDirection(event.target.value as CallDirection)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {CALL_DIRECTIONS.map((one) => (
                  <option key={one} value={one}>
                    {t(`activity.calls.direction.${one}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={`${fieldId}-outcome`}>{t('activity.calls.outcomeLabel')}</Label>
              <select
                id={`${fieldId}-outcome`}
                value={outcome}
                onChange={(event) => setOutcome(event.target.value as CallOutcome)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {CALL_OUTCOMES.map((one) => (
                  <option key={one} value={one}>
                    {t(`activity.outcome.${one}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Only a connected call has a length. Offering the boxes otherwise would invite a
              number the server refuses. */}
          {connected ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium">{t('activity.calls.durationLabel')}</legend>
              <div className="flex items-end gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${fieldId}-minutes`} className="text-xs">
                    {t('activity.calls.minutes')}
                  </Label>
                  <Input
                    id={`${fieldId}-minutes`}
                    type="number"
                    min={0}
                    max={240}
                    inputMode="numeric"
                    value={minutes}
                    onChange={(event) => setMinutes(event.target.value)}
                    className="w-24"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${fieldId}-seconds`} className="text-xs">
                    {t('activity.calls.seconds')}
                  </Label>
                  <Input
                    id={`${fieldId}-seconds`}
                    type="number"
                    min={0}
                    max={59}
                    inputMode="numeric"
                    value={seconds}
                    onChange={(event) => setSeconds(event.target.value)}
                    className="w-24"
                  />
                </div>
              </div>
            </fieldset>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor={`${fieldId}-notes`}>{t('activity.calls.notesLabel')}</Label>
            <Textarea
              id={`${fieldId}-notes`}
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t('activity.calls.notesPlaceholder')}
            />
          </div>

          {/* Said plainly, because it is true and people are used to being able to fix things. */}
          <p className="text-xs text-muted-foreground">{t('activity.calls.permanentHint')}</p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={log.isPending || !subject.trim()}>
              {t('activity.calls.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
